import { useMemo, useCallback } from "react"
import { useSearchParams } from "react-router-dom"
import { CreateOrderDialogFlow, CreateOrderDialogMode, CreateOrderDialogType } from "../createOrderTypes"
import { CreateOrderType } from "../helpers"

export type UrlCreateOrderConfig = Readonly<{
    activeOrderTab?: CreateOrderType
    dialogType?: CreateOrderDialogType
    orderId?: number
    mode?: CreateOrderDialogMode
    flowConfig?: CreateOrderDialogFlow
}>

const PARAM_OPEN = "orderDialog"
const PARAM_TAB = "orderTab"
const PARAM_MODE = "mode"
const PARAM_TYPE = "dialogType"
const PARAM_ORDER_ID = "orderId"
const PARAM_FLOW = "flow" // flow.flow
const PARAM_FLOW_MODE = "flowMode" // flow.mode
const PARAM_FLOW_PARAM = "flowParam" // flow.flowParam

/**
 * Safe value picker: if a whitelist is passed, we check it;
 * if not, we accept the string as a value of type T (required for string union types without runtime representation).
 */
function pickIfIn<T extends string>(val: string | null, allowed?: readonly T[]): T | undefined {
    if (!val) return undefined
    return allowed ? (allowed.includes(val as T) ? (val as T) : undefined) : (val as unknown as T)
}

/** The only whitelist we KNOW for sure at runtime — modal modes */
const CREATE_ORDER_DIALOG_MODES = [
    CreateOrderDialogMode.CREATE,
    CreateOrderDialogMode.EDIT,
    CreateOrderDialogMode.DUPLICATE,
] as const satisfies readonly CreateOrderDialogMode[]

export function useOrderDialogUrlState() {
    const [searchParams, setSearchParams] = useSearchParams()

    const isOpen = searchParams.get(PARAM_OPEN) === "1"

    // The return type is annotated on the callback itself, not on `config` and not as a useMemo
    // type argument — only this form triggers excess-property checking on the returned literal,
    // so a field name missing from UrlCreateOrderConfig fails to compile instead of silently
    // reaching consumers as undefined (which is how `flow` vs `flowConfig` went unnoticed).
    const config = useMemo((): UrlCreateOrderConfig => {
        // raw values from URL
        const rawTab = searchParams.get(PARAM_TAB)
        const rawMode = searchParams.get(PARAM_MODE)
        const rawType = searchParams.get(PARAM_TYPE)
        const rawOrderId = searchParams.get(PARAM_ORDER_ID)
        const rawFlow = searchParams.get(PARAM_FLOW)
        const rawFlowMode = searchParams.get(PARAM_FLOW_MODE)
        const rawFlowParam = searchParams.get(PARAM_FLOW_PARAM)

        // tab / dialogType:do not have a guaranteed runtime list — accept as a string union (with typing)
        const activeOrderTab = pickIfIn<CreateOrderType>(rawTab)
        const dialogType = pickIfIn<CreateOrderDialogType>(rawType)

        // mode: we have a whitelist in runtime
        const mode = pickIfIn<CreateOrderDialogMode>(rawMode, CREATE_ORDER_DIALOG_MODES)

        // orderId: numeric parsing
        const orderIdNum = Number(rawOrderId)
        const orderId = Number.isFinite(orderIdNum) && orderIdNum > 0 ? orderIdNum : undefined

        // flow: This is a three-part object => we only collect it if there is at least one field.
        const flowConfig: CreateOrderDialogFlow | undefined =
            rawFlow || rawFlowMode || rawFlowParam
                ? {
                      flow: pickIfIn<any>(rawFlow), // The type any here is okay — it's our domain union/enum; we accept it as a string.
                      mode: pickIfIn<any>(rawFlowMode),
                      vstoFlowParam: rawFlowParam || undefined,
                  }
                : undefined

        return { activeOrderTab, mode, dialogType, orderId, flowConfig }
    }, [searchParams])

    /**
     * Open modal: set orderDialog=1 and configure parameters.
     * replace: true by default to avoid spamming history.
     */
    const openDialog = useCallback(
        (cfg: UrlCreateOrderConfig, pushHistory = false) => {
            const next = new URLSearchParams(searchParams)
            next.set(PARAM_OPEN, "1")

            if (cfg.activeOrderTab) next.set(PARAM_TAB, cfg.activeOrderTab as string)
            else next.delete(PARAM_TAB)

            if (cfg.mode) next.set(PARAM_MODE, cfg.mode)
            else next.delete(PARAM_MODE)

            if (cfg.dialogType) next.set(PARAM_TYPE, cfg.dialogType as string)
            else next.delete(PARAM_TYPE)

            if (cfg.orderId) next.set(PARAM_ORDER_ID, String(cfg.orderId))
            else next.delete(PARAM_ORDER_ID)

            if (cfg.flowConfig) {
                if (cfg.flowConfig.flow) next.set(PARAM_FLOW, String(cfg.flowConfig.flow))
                else next.delete(PARAM_FLOW)

                if (cfg.flowConfig.mode) next.set(PARAM_FLOW_MODE, String(cfg.flowConfig.mode))
                else next.delete(PARAM_FLOW_MODE)

                if (cfg.flowConfig.vstoFlowParam) next.set(PARAM_FLOW_PARAM, cfg.flowConfig.vstoFlowParam)
                else next.delete(PARAM_FLOW_PARAM)
            } else {
                next.delete(PARAM_FLOW)
                next.delete(PARAM_FLOW_MODE)
                next.delete(PARAM_FLOW_PARAM)
            }

            setSearchParams(next, { replace: !pushHistory })
        },
        [searchParams, setSearchParams],
    )

    /**
     * Close the modal: remove all parameters related to the modal state.
     */
    const closeDialog = useCallback(
        (pushHistory = false) => {
            const next = new URLSearchParams(searchParams)
            next.delete(PARAM_OPEN)
            next.delete(PARAM_TAB)
            next.delete(PARAM_MODE)
            next.delete(PARAM_TYPE)
            next.delete(PARAM_ORDER_ID)
            next.delete(PARAM_FLOW)
            next.delete(PARAM_FLOW_MODE)
            next.delete(PARAM_FLOW_PARAM)
            setSearchParams(next, { replace: !pushHistory })
        },
        [searchParams, setSearchParams],
    )

    return { isOpen, config, openDialog, closeDialog }
}
